import React, { useEffect, useMemo, useState } from "react";

// Icons
import icons from "enums/icons";

// Components
import CustomIcon from "components/CustomIcon";
import { CircledButton, CircledElementProps } from "pages/ProjectDashboard";
import { Box } from "@mui/material";

type TButtonWithCounter = CircledElementProps & {
    count: number;
    shouldShowIcon?: boolean;
    size?: number;
    fontSize?: number;
    colors?: [CircledElementProps["color"], CircledElementProps["color"]];
    callback?: (ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
};

const ButtonWithCounter = ({
    count,
    isDisabled,
    shouldShowIcon,
    size,
    fontSize = 9,
    colors = ["hovered", "secondary"],
    callback,
}: TButtonWithCounter) => {
    const defaultValueIcon = useMemo(() => <CustomIcon icon={icons.eye} sx={{ fontSize: 10 }} />, []);

    const [buttonHovered, setButtonHovered] = useState<number | JSX.Element>(count);

    const handleToggleContent = (isHovered: boolean) => {
        if (isDisabled) return;
        setButtonHovered(isHovered ? defaultValueIcon : count);
    };

    useEffect(() => {
        if (isDisabled) return;
        setButtonHovered(!shouldShowIcon ? count : defaultValueIcon);
    }, [defaultValueIcon, count, isDisabled, shouldShowIcon]);

    return (
        <CircledButton
            fontSize={fontSize}
            isDisabled={isDisabled}
            color={typeof buttonHovered !== "number" ? colors[0] : colors[1]}
            size={size}
            onMouseEnter={() => {
                if (isDisabled) return;
                !shouldShowIcon && handleToggleContent(true);
            }}
            onMouseLeave={() => {
                if (isDisabled) return;
                !shouldShowIcon && handleToggleContent(false);
            }}
            onClick={(ev) => {
                if (isDisabled) return;
                ev.stopPropagation();
                ev.preventDefault();
                handleToggleContent(true);
                callback && callback(ev);
            }}
        >
            <Box display="flex" position="relative">
                <Box
                    sx={{
                        display: "flex",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                    }}
                >
                    {buttonHovered}
                </Box>
            </Box>
        </CircledButton>
    );
};

export default ButtonWithCounter;
